import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { authApi } from '../../api/endpoints'
import { ApiError } from '../../api/client'
import { PageMeta } from '../../components/common/PageMeta'
import { LanguageSwitcher } from '../../components/layout/LanguageSwitcher'
import type { CurrentUser } from '../../types/api'

export function AdminLoginPage({ onSignedIn }: { onSignedIn: (user: CurrentUser) => void }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const signIn = useMutation({
    mutationFn: () => authApi.login(email.trim(), password),
    onSuccess: onSignedIn,
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    signIn.mutate()
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: 'background.default' }}>
      <PageMeta title={t('admin.signInTitle')} path="/admin" noIndex />

      <Container maxWidth="xs">
        <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <Box aria-hidden sx={{ fontSize: '2.5rem' }}>
            🛶
          </Box>
          <Typography variant="h3">{t('admin.signInTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('admin.signInBody')}
          </Typography>
        </Stack>

        <Paper
          component="form"
          onSubmit={submit}
          elevation={0}
          sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 4 }}
        >
          <Stack spacing={2}>
            <TextField
              required
              autoFocus
              type="email"
              label={t('admin.email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
            />
            <TextField
              required
              type={showPassword ? 'text' : 'password'}
              label={t('admin.password')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        // A password that cannot be read back is how a correct one gets typed
                        // wrong three times on a phone. Off by default; the choice is the
                        // person's, not ours.
                        onClick={() => setShowPassword((shown) => !shown)}
                        edge="end"
                        aria-label={showPassword ? t('admin.hidePassword') : t('admin.showPassword')}
                        title={showPassword ? t('admin.hidePassword') : t('admin.showPassword')}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            {signIn.isError && (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {signIn.error instanceof ApiError && signIn.error.status === 401
                  ? t('admin.signInFailed')
                  : t('common.errorBody')}
              </Alert>
            )}

            <Button type="submit" variant="contained" size="large" disabled={signIn.isPending}>
              {signIn.isPending ? t('common.loading') : t('admin.signIn')}
            </Button>
          </Stack>
        </Paper>

        <Stack alignItems="center" sx={{ mt: 2 }}>
          <LanguageSwitcher />
        </Stack>
      </Container>
    </Box>
  )
}
